import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { API_BASE } from '../../config/api';

const Scrim = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 1001;
  /* No backdrop-filter: re-blurring the whole page every frame made the open animation stutter. */
  background: rgba(4, 5, 6, 0.78);
`;

const Drawer = styled(motion.aside)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1002;
  width: min(27.5rem, 100%);
  background: var(--surface-1);
  border-left: 1px solid var(--border);
  box-shadow: -12px 0 32px rgba(0, 0, 0, 0.35);
  will-change: transform;
  display: flex;
  flex-direction: column;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border);

  h2 {
    font-size: 1.5rem;
    letter-spacing: -0.01em;
  }

  > div {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
  }

  span {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--text-tertiary);
  }
`;

const IconButton = styled.button`
  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--border-accent);
  border-radius: 0.375rem;
  color: var(--text-primary);

  &:hover {
    border-color: var(--text-tertiary);
  }
`;

const Items = styled.ul`
  list-style: none;
  padding: 0.5rem 1.5rem;
  flex-grow: 1;
  overflow-y: auto;
`;

const Item = styled.li`
  display: flex;
  gap: 1rem;
  padding: 1.125rem 0;
  border-bottom: 1px solid var(--border);
`;

const Thumb = styled.div`
  width: 4.5rem;
  height: 4.5rem;
  flex-shrink: 0;
  border-radius: 0.5rem;
  background: var(--surface-3);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ItemInfo = styled.div`
  flex-grow: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  > div {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    font-weight: 600;
  }

  strong {
    font-family: var(--font-display);
    font-weight: 600;
    white-space: nowrap;
  }

  > span {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--text-tertiary);
  }

  button {
    align-self: flex-start;
    margin-top: 0.25rem;
    padding: 0.375rem 0;
    background: none;
    color: var(--text-secondary);
    font-size: 0.8125rem;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  button:hover {
    color: var(--error);
  }
`;

const Checkout = styled.div`
  padding: 1.5rem;
  border-top: 1px solid var(--border);
  background: var(--card);
  display: flex;
  flex-direction: column;
  gap: 1.125rem;
`;

const Total = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  color: var(--text-secondary);

  strong {
    font-family: var(--font-display);
    font-size: 1.875rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: var(--text-primary);
  }
`;

const Notice = styled.div`
  display: flex;
  gap: 0.625rem;
  align-items: flex-start;
  font-size: 0.875rem;
  color: var(--text-secondary);
  padding: 0.75rem 0.875rem;
  border: 1px solid var(--border-accent);
  border-radius: 0.375rem;
`;

const PrimaryButton = styled.button`
  min-height: 3.25rem;
  background: var(--brand);
  color: var(--brand-ink);
  border-radius: 0.375rem;
  font-weight: 600;

  &:hover {
    background: #ff8f4f;
  }
`;

const Small = styled.p`
  text-align: center;
  font-size: 0.875rem;
  color: var(--text-tertiary);

  a {
    color: var(--text-primary);
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const Empty = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.875rem;
  padding: 2.5rem 2rem;
  text-align: center;

  > div {
    width: 4rem;
    height: 4rem;
    border-radius: 50%;
    border: 1px solid var(--border-accent);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-tertiary);
  }

  h3 {
    font-size: 1.375rem;
    margin-top: 0.375rem;
  }

  p {
    max-width: 17.5rem;
    margin-bottom: 0.625rem;
  }

  a {
    padding: 0.75rem 1.25rem;
    border: 1px solid var(--border-accent);
    border-radius: 0.375rem;
    font-weight: 500;
  }
`;

const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;

const Cart = ({ isOpen, onClose }) => {
  const { cart, removeFromCart, getCartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  // PayPal mounts heavy iframes; wait until the slide-in finishes so it does not stutter.
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!isOpen) setSettled(false);
  }, [isOpen]);

  const isLoggedIn = !!user;
  const itemCount = cart.reduce((n, item) => n + item.quantity, 0);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleLogin = () => {
    localStorage.setItem('pendingCart', JSON.stringify(cart));
    onClose();
    navigate('/login');
  };

  const createOrder = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/create-paypal-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ items: cart }),
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      return data.id;
    } catch (error) {
      console.error('Error creating order:', error);
      return null;
    }
  };

  const onApprove = async (data) => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_BASE}/api/capture-paypal-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ orderID: data.orderID }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Payment capture failed');
      }

      const captureData = await response.json();
      if (captureData.status === 'COMPLETED') {
        clearCart();
        onClose();
        navigate('/dashboard');
      } else {
        throw new Error('Payment not completed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('There was an error processing your payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Scrim
            key="scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <Drawer
            key="drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Cart"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.25 }}
            onAnimationComplete={() => setSettled(true)}
          >
            <Head>
              <div>
                <h2>Your cart</h2>
                <span>{itemCount === 1 ? '1 item' : `${itemCount} items`}</span>
              </div>
              <IconButton type="button" onClick={onClose} aria-label="Close cart" autoFocus>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </IconButton>
            </Head>

            {cart.length === 0 ? (
              <Empty>
                <div>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="9" cy="20" r="1.5" />
                    <circle cx="18" cy="20" r="1.5" />
                    <path d="M2 3h3l2.6 12.4a1.5 1.5 0 0 0 1.5 1.1h8.8a1.5 1.5 0 0 0 1.5-1.2L21 8H6" />
                  </svg>
                </div>
                <h3>Your cart is empty</h3>
                <p>Items you add from the shop are listed here.</p>
                <Link to="/shop" onClick={onClose}>
                  Browse the shop
                </Link>
              </Empty>
            ) : (
              <>
                <Items>
                  {cart.map((item) => (
                    <Item key={item.id}>
                      <Thumb>{item.image && <img src={item.image} alt="" />}</Thumb>
                      <ItemInfo>
                        <div>
                          <span>{item.title}</span>
                          <strong>{formatPrice(item.price * item.quantity)}</strong>
                        </div>
                        <span>Digital download · qty {item.quantity}</span>
                        <button type="button" onClick={() => removeFromCart(item.id)}>
                          Remove
                        </button>
                      </ItemInfo>
                    </Item>
                  ))}
                </Items>

                <Checkout>
                  <Total>
                    <span>Total</span>
                    <strong>{formatPrice(getCartTotal())}</strong>
                  </Total>

                  {!isLoggedIn ? (
                    <>
                      <Notice>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }}>
                          <rect x="4" y="11" width="16" height="10" rx="2" />
                          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                        </svg>
                        <span>Log in to pay. Your cart is saved, and the files appear in your dashboard after payment.</span>
                      </Notice>
                      <PrimaryButton type="button" onClick={handleLogin}>
                        Log in to purchase
                      </PrimaryButton>
                      <Small>
                        No account?{' '}
                        <Link to="/register" onClick={onClose}>
                          Create one
                        </Link>
                      </Small>
                    </>
                  ) : isProcessing ? (
                    <Small role="status">Processing your payment…</Small>
                  ) : !settled ? (
                    <div style={{ minHeight: '9.5rem' }} aria-hidden="true" />
                  ) : (
                    <>
                      <PayPalButtons
                        createOrder={createOrder}
                        onApprove={onApprove}
                        onCancel={() => setIsProcessing(false)}
                        onError={(err) => {
                          console.error('PayPal error:', err);
                          setIsProcessing(false);
                        }}
                        style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' }}
                      />
                      <Small style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>
                        Secure payment via PayPal · USD
                      </Small>
                    </>
                  )}
                </Checkout>
              </>
            )}
          </Drawer>
        </>
      )}
    </AnimatePresence>
  );
};

export default Cart;
